<template>
  <div class="expertise-step">
    <h2 class="step-title">Your Expertise</h2>
    <p class="step-description">Help us understand your specialization and expertise level</p>
    
    <!-- Loading state -->
    <div v-if="loadingFormOptions" class="loading-container">
      <v-progress-circular indeterminate color="primary" size="40" />
      <p>Loading expertise options...</p>
    </div>
    
    <div v-else class="expertise-content">
      <!-- Expertise Level -->
      <div class="form-group">
        <label class="expertise-label">Expertise Level*</label>
        <div class="expertise-levels">
          <div 
            v-for="level in expertiseLevels" 
            :key="level.value"
            class="level-card"
            :class="{ selected: expertiseData.expertiseLevel === level.value }"
            @click="selectExpertiseLevel(level.value)"
          >
            <div class="level-icon">
              <v-icon size="large">{{ level.icon }}</v-icon>
            </div>
            <div class="level-name">{{ level.name }}</div>
            <div class="level-description">{{ level.description }}</div>
          </div>
        </div>
        <div v-if="errors.expertiseLevel" class="error-message">{{ errors.expertiseLevel }}</div>
      </div>
      
      <!-- Expertise Areas -->
      <MultiSelectWithOther
        label="Your Expertise Areas"
        description="Select all areas where you have expertise"
        :options="expertiseAreas"
        v-model="selectedExpertiseAreas"
        v-model:other-value="otherExpertise"
        :allow-other="true"
        other-placeholder="Specify any additional expertise..."
        option-key="id"
        option-label="name"
        :error="errors.expertiseAreas"
      />
      
      <!-- Industries -->
      <MultiSelectWithOther
        label="Industries you have experience with"
        description="Select all industries you have worked in"
        :options="industries"
        v-model="selectedIndustries"
        v-model:other-value="otherIndustries"
        :allow-other="true"
        other-placeholder="Specify any additional industries..."
        option-key="id"
        option-label="name"
        :error="errors.industries"
      />
      
      <div class="action-buttons">
        <button 
          class="btn btn-back"
          @click="handleBack"
          :disabled="isSubmitting"
        >
          <v-icon>mdi-arrow-left</v-icon> Back
        </button>
        
        <button 
          class="btn btn-continue"
          @click="handleContinue"
          :disabled="isSubmitting"
        >
          <template v-if="isSubmitting">
            <v-progress-circular indeterminate color="white" size="20" class="spinner" />
          </template>
          <template v-else>
            Continue <v-icon>mdi-arrow-right</v-icon>
          </template>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import MultiSelectWithOther from '@/components/common/MultiSelectWithOther.vue';
import apiService from '@/services/apiService';

export default {
  name: 'RegisterStep6',
  components: {
    MultiSelectWithOther
  },
  props: {
    formData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      expertiseData: {
        expertiseLevel: this.formData.expertiseLevel || null
      },
      formOptions: this.formData.formOptions || {},
      loadingFormOptions: false,
      selectedExpertiseAreas: this.formData.expertiseAreas || [],
      otherExpertise: this.formData.otherExpertise || '',
      selectedIndustries: this.formData.industries || [],
      otherIndustries: this.formData.otherIndustries || '',
      isSubmitting: false,
      error: '',
      generalError: '',
      errors: {
        expertiseLevel: '',
        expertiseAreas: '',
        industries: ''
      }
    };
  },
  computed: {
    expertiseLevels() {
      // Convert backend expertise levels to the format expected by the UI
      const backendLevels = this.formOptions.expertiseLevels || [];
      return backendLevels.map(level => {
        const levelName = level.name.toLowerCase();
        let icon = 'mdi-star';
        let description = level.description || `${level.name} level expertise`;
        let dbValue = level.name.toLowerCase(); // Default to lowercase name
        
        // Map icons and database values based on level name
        if (levelName.includes('junior') || levelName.includes('beginner')) {
          icon = 'mdi-lightning-bolt';
          description = description || '1-3 years of experience';
          dbValue = 'junior';
        } else if (levelName.includes('mid') || levelName.includes('intermediate')) {
          icon = 'mdi-star';
          description = description || '3-5 years of experience';
          dbValue = 'mid';
        } else if (levelName.includes('senior') || levelName.includes('expert')) {
          icon = 'mdi-crown';
          description = description || '5+ years of experience';
          dbValue = 'senior';
        }
        
        return {
          value: dbValue, // Use database value ('junior', 'mid', 'senior')
          name: level.name, // Display name ('Junior', 'Mid-level', 'Senior')
          icon: icon,
          description: description
        };
      });
    },
    expertiseAreas() {
      return this.formOptions.expertiseAreas || [];
    },
    industries() {
      return this.formOptions.industries || [];
    }
  },
  async mounted() {
    // If form options weren't passed from previous step, fetch them
    if (!this.formOptions.brands || !this.formOptions.expertiseAreas) {
      await this.fetchFormOptions();
    }
  },
  methods: {
    async fetchFormOptions() {
      this.loadingFormOptions = true;
      try {
        const response = await apiService.get('experts/form-options/');
        this.formOptions = response.data;
        console.log('Form options loaded in step 6:', this.formOptions);
      } catch (error) {
        console.error('Failed to load form options:', error);
        this.generalError = 'Failed to load form options. Please refresh the page and try again.';
      } finally {
        this.loadingFormOptions = false;
      }
    },
    selectExpertiseLevel(level) {
      this.expertiseData.expertiseLevel = level;
      this.errors.expertiseLevel = '';
    },
    validateForm() {
      let isValid = true;
      this.errors = {
        expertiseLevel: '',
        expertiseAreas: '',
        industries: ''
      };
      this.error = '';
      this.generalError = '';
      
      // Validate expertise level
      if (!this.expertiseData.expertiseLevel) {
        this.errors.expertiseLevel = 'Please select your expertise level';
        isValid = false;
      }
      
      // Validate expertise areas - at least one must be selected or other text provided
      if (this.selectedExpertiseAreas.length === 0 && (!this.otherExpertise || this.otherExpertise.trim() === '')) {
        this.errors.expertiseAreas = 'Please select at least one expertise area or specify other expertise';
        isValid = false;
      }
      
      // Validate industries - at least one must be selected or other text provided
      if (this.selectedIndustries.length === 0 && (!this.otherIndustries || this.otherIndustries.trim() === '')) {
        this.errors.industries = 'Please select at least one industry or specify other industries';
        isValid = false;
      }
      
      return isValid;
    },
    handleBack() {
      this.$emit('prev-step');
    },
    handleContinue() {
      if (this.validateForm()) {
        this.isSubmitting = true;
        
        // Get the actual expertise area objects for the selected IDs
        const selectedExpertiseObjects = this.expertiseAreas.filter(area => 
          this.selectedExpertiseAreas.includes(area.id)
        );
        
        // Get the actual industry objects for the selected IDs
        const selectedIndustryObjects = this.industries.filter(industry => 
          this.selectedIndustries.includes(industry.id)
        );
        
        const updatedData = {
          ...this.formData,
          expertiseLevel: this.expertiseData.expertiseLevel,
          expertiseAreas: this.selectedExpertiseAreas,
          expertiseAreasDetails: selectedExpertiseObjects,
          otherExpertise: this.otherExpertise,
          industries: this.selectedIndustries,
          industriesDetails: selectedIndustryObjects,
          otherIndustries: this.otherIndustries,
          // Pass form options to next steps
          formOptions: this.formOptions
        };
        
        // Emit updated form data
        this.$emit('update:form-data', updatedData);
        
        // Move to next step
        this.$emit('next-step');
        
        this.isSubmitting = false;
        this.generalError = '';
      }
    }
  }
};
</script>

<style scoped>
.expertise-step {
  background: white;
  border-radius: 8px;
  padding: 30px;
  max-width: 900px;
  margin: 0 auto;
}

.step-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  text-align: center;
}

.step-description {
  text-align: center;
  color: #666;
  margin-bottom: 30px;
}

.loading-container {
  text-align: center;
  padding: 40px;
  color: #666;
}

.loading-container p {
  margin-top: 15px;
}

.expertise-content {
  padding: 0 10px;
}

.form-group {
  margin-bottom: 30px;
}

label {
  display: block;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 10px;
  color: #333;
}

.expertise-label {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 15px;
}

.form-control {
  width: 100%;
  padding: 12px 15px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #fff;
}

/* Expertise Levels */
.expertise-levels {
  display: flex;
  gap: 20px;
  margin-bottom: 10px;
}

.level-card {
  flex: 1;
  padding: 20px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.level-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.level-card.selected {
  border-color: #e94057;
  background-color: rgba(233, 64, 87, 0.05);
}

.level-icon {
  margin-bottom: 15px;
  color: #333;
}

.level-card.selected .level-icon {
  color: #e94057;
}

.level-name {
  font-weight: 600;
  font-size: 18px;
  margin-bottom: 5px;
}

.level-description {
  font-size: 14px;
  color: #666;
}

.action-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
}

.btn {
  padding: 15px 30px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
}

.btn-back {
  background-color: #f0f0f0;
  color: #333;
}

.btn-back:hover {
  background-color: #e0e0e0;
}

.btn-continue {
  background-color: #e94057;
  color: white;
}

.btn-continue:hover {
  background-color: #d03a4e;
}

.btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.error-message {
  color: #e94057;
  font-size: 14px;
  margin-top: 5px;
}

.general-error {
  text-align: center;
  margin-top: 20px;
}

.spinner {
  margin: 0;
}

@media (max-width: 768px) {
  .expertise-levels {
    flex-direction: column;
  }
}

.mt-2 { margin-top: 8px; }

/* Custom select wrapper to show icon */
.select-wrapper {
  position: relative;
}

.select-icon {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #666;
}

.select-dropdown::-ms-expand { display: none; }
.select-dropdown {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}
</style> 